/* Серверный режим экрана «Фотосессия товара» на движке шаблона (план вехи 3, шаги 2–4 экрана; lane plan shoot §1.2, §2).
   createPshoot(createPshootServer) (features/pshoot/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SRV спрашивает этот объект:
   категории, сцены, наборы, площадки, форматы, число фото и чипы — из документа экрана (GET /app/studio/screens/pshoot) и формы; цена,
   готовность, создание, хвост и итог — только из сервиса заказа кадра (OrderService привязки EngineFrame). Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние формы (S), свои фото товара, свои фото сцен, свой блок «Съёмка», своё зеркало входов и свой хвост BotRun;
   - S — вид входов сервиса: входы изменились не формой (повтор из «Истории», заявка родной формы, «Изменить») — readPshoot → S; правка
     формы — writePshoot → входы (одна точка записи — syncFoot шаблона), только опубликованными ключами и кодированием родной формы
     (pshoot-inputs.ts), поэтому заявки обеих форм читаются друг другом (I1, откат);
   - фото товара — лоток моста загрузок (media-bridge: лист согласия, одна очередь, слоты photo…photo_5 подряд, «Сделать главным» и «Убрать»
     без новой загрузки); S.files — только его записи; свои фото сцен — BotRef с проверкой сервера (ref-bridge); «Съёмка» — BotCam только при
     совпадении словарей с сервером (cam-bridge);
   - модель — один выбор ряда «Кто в кадре» (BotPeople в режиме «модель»): model / person_id / avatar_id / me (people-bridge modelOf), «Вы» —
     только готовый, близкий — только сохранённый и доступный, «Модель ИИ» — только если форма публикует gen;
   - сцены и наборы — картинки сервера (scene image_url и examples); товар в сцены на клиенте не вклеивается (решение Q-S2), демо-рисунков,
     демо-лица и угадывания категории по имени файла нет (D12);
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис;
   - цена на кнопке — цена кадра × кадры (или скидка формы); в заказ уходит цена сервиса; если они расходятся у платной работы, создание
     закрыто: «Цена этого выбора пока не подтверждена» (D11);
   - итог — кадры заказа через owner-protected маршрут (result-gallery) по секциям ответа; «Скачать всё» — архив сервера или zip кадров;
     «Слайды с преимуществами», «Видео товара, 5 сек» — переход после сверки владельца (result-handoff), «Ещё вариант» и «Ещё сцены» — к форме
     с теми же настройками без нового заказа (D10). */
import { endpoints } from '../../api';
import { uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance, toolPrice } from '../studio/circle/tool-helpers';
import { tailProgress } from '../studio/circle/order-tail-model';
import { studioCheapOffer, type StudioApp, type StudioForm } from '../studio/studio-api';
import { record } from '../studio/studio-content';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { createCamBridge, type CamBlock, type CamBridge } from '../studio/engine-kit/cam-bridge';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { isImproveKind } from '../studio/engine-kit/improve-service';
import { createInputsMirror, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaFile, mediaPolicy, type MediaBridge, type MediaItem, type MediaTray } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { UploadRejectedError } from '../../api/uploads';
import { modelOf, peopleModule, personRef, SILHOUETTE, watchPeople, type PeopleModule, type SelItem, type SnapPick } from '../studio/engine-kit/people-bridge';
import { createRefBridge, type RefBridge, type RefFile, type RefOpts } from '../studio/engine-kit/ref-bridge';
import { archive, archiveUrl, createResultGallery, galleryKey, ORDER_IMAGE_LIMITS, sections, type ResultGallery } from '../studio/engine-kit/result-gallery';
import { handoffTarget } from '../studio/engine-kit/result-handoff';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { screenDoc, type ScreenDoc } from '../studio/engine-kit/screen-doc';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { allowed, fieldOf, photoSlots, plural, requiredGap, valueOf } from '../shoot/kit/form-model';
import { pshootKeys, splitScenes } from './pshoot-model';
import {
  defaultSet,
  pshootLists,
  pshootSupported,
  rawField,
  readPshoot,
  viewScenes,
  writePshoot,
  type EngineScene,
  type EngineSet,
  type PlatRow,
  type PshootLists,
  type PshootView,
} from './pshoot-inputs';

/* ---------- контракт с движком (features/pshoot/engine.ts, serve({...})) ---------- */

/** Фото товара в S.files: запись лотка моста; st — cut (грузится, проверяется) | ok (готово) */
export interface PshootFile {
  id: string;
  name: string;
  size: number;
  url: string;
  w: number;
  h: number;
  st: 'cut' | 'ok';
}
/** Состояние формы шаблона (S) */
export interface PshootState {
  cat: string;
  setK: Record<string, string>;
  own: Record<string, string[]>;
  focus: string | null;
  plat: string;
  fmt: string;
  platVia: boolean;
  n: number;
  people: SelItem[];
  extra: unknown[];
  mc: string | null;
  files: PshootFile[];
  text: string;
  cheap: boolean;
  menu: string | null;
  sh: { view: string; [key: string]: unknown } | null;
  ret: boolean;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** Ряд лиц «Кто в кадре» (BotPeople.attach, режим «модель») */
interface EnginePeople {
  paintForm(): void;
  prune(): void;
  close(): void;
  setIn(on: boolean): void;
  readonly isOpen: boolean;
}
/** Свои фото сцен (BotRef.make, multi 9) */
interface EngineRef {
  active(): boolean;
  busy(): boolean;
  files(): Array<{ upload_id: string | null; st: string; url?: string }>;
  order(): Array<{ upload_id: string | null; use?: string }>;
  restore(s: { files: RefFile[] } | null): void;
  unpick(o?: { quiet?: boolean }): void;
  say(text: string, el?: Element | null): void;
}
interface EngineCam extends CamBlock {
  reset(k?: string): void;
}
/** Рамка загрузки (BotCore.upload) */
interface EngineUp {
  o: Record<string, unknown>;
  say(text: string): void;
}
/** Крючки, которые движок отдаёт serve (engine.ts, вставка после состояния монтажа): tail, foot, up, people, ref, cam, sc и gs — геттеры */
export interface PshootEngineHooks {
  S: PshootState;
  CATS: string[][];
  SCENES: EngineScene[];
  SETS: Record<string, EngineSet[]>;
  MPS: string[][];
  FMTS: unknown[][];
  PLAT: PlatRow[];
  NS: number[];
  THEMES: string[];
  WISH: string[][];
  readonly tail: BotRunApi;
  readonly foot: EngineFoot;
  readonly up: EngineUp;
  readonly people: EnginePeople;
  readonly ref: EngineRef;
  readonly cam: EngineCam;
  readonly sc: { sync(o?: { force?: boolean }): void; close(): void };
  readonly gs: unknown;
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  setLimits(o: { files?: number; mb?: number; scenes?: number; wishes?: number; side?: number }): void;
  limits(): { files: number; scenes: number; wishes: number; side: number };
  paintPage(): void;
  paintUp(): void;
  railSync(): void;
  railPaint(reset?: boolean): void;
  setSync(): void;
  filesChanged(): void;
  syncFoot(): void;
  sheetOpen(view: string, o?: Record<string, unknown>): void;
  sheetClose(): void;
  menuClose(): void;
  camStop(): void;
  runExit(prep?: () => void): void;
  nNow(): number;
  sel(): string[];
  setK(): string;
  pick(): SnapPick;
  pickName(p?: SnapPick): string;
  plat(): PlatRow;
  ar(): string;
  mpName(): string;
  split(n: number, k: number): number[];
}
type Missing = 'load' | 'blocked' | 'off' | 'unavailable' | 'docs' | '';
/** Группа кадров итога (сцена набора или «По вашим фото») */
export interface PshootGroup {
  id: string;
  n: string;
  k: number;
}
/** Снимок входа заказа (r.snap BotRun, runSnap шаблона): из тела заявки, а без неё — из формы; у готового — по секциям итога.
    idx — номер файла итога для кадра k (resPic) */
export interface PshootSnap {
  [key: string]: unknown;
  cat: string;
  item: string;
  plat: string;
  fmt: string;
  pn: string;
  ar: string;
  px: [number, number];
  n: number;
  set: string;
  sc: PshootGroup[];
  files: [];
  model: string;
  m: null;
  mn: string;
  text: string;
  cheap: boolean;
  idx: number[];
}
type Slot = 'format' | 'count' | 'platform' | 'shoot' | 'wishes' | 'model' | 'gen' | 'char' | 'scenes';

export interface PshootServer {
  ui<U extends object>(base: U): ServerUi<U>;
  entry(o: unknown): Record<string, unknown>;
  open(o: object): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T, extra?: unknown): T;
  has(k: Slot): boolean;
  refOn(): boolean;
  refOpts(): RefOpts;
  defSet(cat: string): string;
  gen(): { img: string; y: number };
  shot(o: { sc?: (Partial<EngineScene> & { bg?: string }) | null; v?: number; cls?: string }): string;
  exN(sc: Partial<EngineScene> | null): number;
  example(): { before: string; after: string; labels: string[]; title: string; text: string } | null;
  upOpts(): Record<string, unknown>;
  setImg(): string;
  camBase(): { img: string; prod: string };
  unit(): string;
  nUnit(n: number): string;
  improveKind(): string;
  price(cheap: boolean): number;
  missing(): Missing | null;
  ready(): string;
  missText(kind: string): string;
  label(): string | null;
  write(): void;
  pick(files: FileList | File[] | null | undefined): void;
  remove(i: number): void;
  main(i: number): void;
  replace(i: number, file: File): void;
  camText(): string;
  start(btn: HTMLElement): void;
  steps(): string[][];
  snap(): PshootSnap;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  /** Подпись итога (T.doneP): доставка заказа — бот уже прислал, пришлёт или не присылает (tail-done) */
  doneText(r: BotRunRun): string;
  resPic(k: number): string;
  /** Подписи кнопок лайтбокса ядра (BotCore.rv.lightbox: «Скачать», «Закрыть», «Предыдущий», «Следующий» — без текстового шва L-TX) */
  lbText(): void;
  save(k: number): Promise<{ data: Blob; name: string }>;
  saveAll(btn: HTMLElement): void;
  next(screen: 'infog' | 'video'): void;
  leave(go: () => void): void;
  click(d: DOMStringMap, el: HTMLElement, r: unknown): boolean;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest?: string): string };
  saveBtn?(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: object): void;
  zip?(files: Array<{ name: string; data: Blob }>): Promise<Blob>;
  rv?: { lightbox?: { isOpen: boolean; close(): void } };
  improve?: { isOpen: boolean; close(): void };
  editor?: { isOpen: boolean; close(): void };
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
const TRANSPORT = uploadFailureText(null);
/** Текст окончательного отказа проверки загрузки (UploadRejectedError) — его называет сам мост загрузок у файла */
const REJECTED = uploadFailureText(Object.create(UploadRejectedError.prototype));
/** Кадров в итоге фотосессии товара (родной хвост: OrderTail limit 48) */
const RESULT_LIMITS = { ...ORDER_IMAGE_LIMITS, limit: 48 };
/** Этапы шаблона (runSteps bot-pshoot.js) и ключи этапов сервера там, где они другие (MessagingOrderSteps: bg → «Убираем фон», scene → «Снимаем сцены») */
const STEP_ALIAS = { bg: 'cut', scene: 'shoot' };
const FAIL = { modH: 'Пожелания не прошли проверку', techH: 'Не получилось снять товар', notMade: 'фото не созданы' };
const CONSENT = { row: 'Права на фото товара', sub: 'Подтверждаю право использовать фото товара' };
const photosW = (n: number) => plural(n, uiText('фото'), uiText('фото'), uiText('фото'));
const pad = (n: number) => String(n).padStart(2, '0');
const extOf = (mime: string) => (mime === 'image/png' ? '.png' : mime === 'image/webp' ? '.webp' : '.jpg');
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** BotPeople для ряда лиц: у записи без фото (демо-люди шаблона до server-state) — пустой список фото */
function safePeople(): PeopleModule | null {
  const m = peopleModule();
  if (!m) return null;
  return { ...m, store: { all: () => m.store.all().map(p => ({ ...p, photos: Array.isArray(p.photos) ? p.photos : [] })) } };
}

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  mirror: InputsMirror;
  driver: RunDriver;
  media: MediaBridge;
  /** Лоток фото товара (слоты photo…photo_5 формы) */
  tray: MediaTray | null;
  ref: RefBridge;
  cam: CamBridge;
  gallery: ResultGallery;
  doc: ScreenDoc<Record<string, unknown>>;
  consent: ConsentSheet | null;
  /** Форма и документ, из которых собраны списки (lists) */
  form: StudioForm | null;
  data: Record<string, unknown> | undefined;
  lists: PshootLists | null;
  /** Входы уже перенесены в S (вход экрана) */
  entered: boolean;
  /** Раскладка формы на экране: опубликованные части и списки */
  layout: string;
  /** id фото повтора, превью которых уже читались */
  restored: string;
  /** Превью своих фото сцен из входов читается: их ключи и set_id 'ref' остаются во входах */
  refRestoring: boolean;
  camAsked: boolean;
  camDone: boolean;
  said: string;
  peek: Intent | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  /** Заказ и ревизия, кадры которых уже на итоге */
  doneKey: string;
  doneRun: BotRunRun | null;
  moving: boolean;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createPshootServer(h: PshootEngineHooks): PshootServer {
  const { S } = h;
  /* демо-категорий, сцен, наборов и чипов шаблона в серверном режиме нет: списки — только из документа экрана */
  h.CATS.splice(0);
  h.SCENES.splice(0);
  for (const key of Object.keys(h.SETS)) delete h.SETS[key];
  h.THEMES.splice(0);
  h.WISH.splice(0);
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относится S (форма шаблона) — объект, а не ключ: ключи ServerStudio повторяются после нового входа в раздел */
  let painted: Frame | null = null;
  /** Кадр, из документа которого сейчас списки шаблона (CATS, SCENES…) */
  let filled: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };

  /* ---- списки шаблона из документа экрана ---- */
  function fillLists(f: Frame) {
    filled = f;
    const L = f.lists;
    if (!L) return;
    h.CATS.splice(0, h.CATS.length, ...L.cats.map(item => item.slice()));
    /* метки «Хит» и «Новое» — на языке профиля (смена языка заполняет списки заново) */
    h.SCENES.splice(0, h.SCENES.length, ...L.scenes.map(scene => ({ ...scene, b: uiText(scene.b), th: scene.th.slice(), sp: { ...scene.sp, c: scene.sp.c.slice() }, ex: scene.ex.slice() })));
    for (const key of Object.keys(h.SETS)) delete h.SETS[key];
    for (const [cat, list] of Object.entries(L.sets)) h.SETS[cat] = list.map(item => ({ ...item, b: uiText(item.b), sc: item.sc.slice() }));
    h.MPS.splice(0, h.MPS.length, ...L.mps.map(item => item.slice()));
    h.FMTS.splice(0, h.FMTS.length, ...L.fmts.map(([k, n, a, w, hh, s]) => [k, uiText(n), a, w, hh, uiText(s)]));
    h.PLAT.splice(0, h.PLAT.length, ...L.plat.map(([k, n, a, w, hh, s]) => (s === undefined ? [k, n, a, w, hh] : [k, uiText(n), a, w, hh, uiText(s)]) as PlatRow));
    h.NS.splice(0, h.NS.length, ...L.counts);
    h.THEMES.splice(0, h.THEMES.length, ...L.themes);
    h.WISH.splice(0, h.WISH.length, ...L.wishes.map(item => item.slice()));
    const policy = f.tray ? mediaPolicy(f.svc.get().form, f.tray.keys()[0]) : null;
    h.setLimits({ files: L.limits.files, mb: policy ? Math.round(policy.maxBytes / MB) : 0, scenes: L.limits.scenes, wishes: L.limits.wishes, side: L.limits.side });
  }

  /* ---- фото товара: записи лотка моста → S.files ---- */
  /** Размеры снимка: что прочитал сервер, что прочитал мост, иначе своё чтение (один раз на адрес) — для пометки мелкого фото */
  const sizes = new Map<string, { w: number; h: number } | null>();
  function sizeOf(f: Frame, item: MediaItem): { w: number; h: number } {
    const media = item.check?.media;
    if (media?.width && media.height) return { w: media.width, h: media.height };
    if (item.meta.width && item.meta.height) return { w: item.meta.width, h: item.meta.height };
    const known = sizes.get(item.url);
    if (known !== undefined) return known ?? { w: 0, h: 0 };
    sizes.set(item.url, null);
    if (item.file)
      void mediaFile.readImageSize(item.file).then(size => {
        if (!size) return;
        sizes.set(item.url, { w: size.width, h: size.height });
        if (f.tray?.list().some(it => it.url === item.url)) trayChanged(f);
      });
    return { w: 0, h: 0 };
  }
  function filesOf(f: Frame): PshootFile[] {
    return (f.tray?.list() ?? [])
      .filter(item => !!item.url)
      .map(item => ({ id: item.id, name: item.name, size: item.size, url: item.url, ...sizeOf(f, item), st: item.state === 'ready' ? 'ok' : 'cut' }));
  }
  /** Лоток моста изменился: S.files заново, рамка, мозаики наборов, кнопка */
  function trayChanged(f: Frame) {
    if (cur() !== f) return;
    S.files = filesOf(f);
    if (!onForm()) return;
    h.filesChanged();
  }
  /** S.files — те же записи, что в лотке (мост убрал или переставил запись без своего отклика) */
  function filesSync(f: Frame) {
    const now = filesOf(f);
    if (now.length !== S.files.length || now.some((file, i) => file.id !== S.files[i]?.id || file.url !== S.files[i]?.url || file.st !== S.files[i]?.st || file.w !== S.files[i]?.w))
      trayChanged(f);
  }
  /** Фото повтора: id слотов во входах, которых нет в лотке, — превью (один раз на набор); записи, которых больше нет во входах, уходят */
  function photosSync(f: Frame) {
    const tray = f.tray;
    if (!tray) return;
    const { s, d } = live(f);
    const ids = tray.keys().map(key => s.inputs[key]).filter((id): id is string => !!id);
    const list = tray.list();
    const known = (id: string) => list.some(item => item.uploadId === id || item.pendingId === id);
    if (ids.length && !ids.every(known) && f.restored !== ids.join()) {
      f.restored = ids.join();
      void tray.restore(ids).finally(() => {
        if (cur() === f && h.mounted()) changed(f);
      });
      return;
    }
    if (d.locked || f.media.pending()) return;
    for (const item of list) if (item.state === 'ready' && item.uploadId && !ids.includes(item.uploadId)) tray.remove(item.id);
  }
  /** Права на фото не подтверждены, а фото повтора стоят в лотке: кнопка подтверждает их (лист согласия без файла) */
  const rightsNeeded = (f: Frame) => !f.svc.get().rights && !!f.tray?.list().some(item => item.state === 'ready');
  function confirmRights(f: Frame) {
    f.consent?.close();
    f.consent = openConsentSheet({
      rights: () => f.svc.get().rights,
      locked: () => f.svc.derive().locked,
      setRights: on => f.svc.setters.rights(on),
      confirm: true,
      texts: CONSENT,
      onContinue: () => {
        f.consent = null;
        if (cur() === f) h.syncFoot();
      },
      onClose: () => {
        f.consent = null;
      },
    });
  }

  /* ---- входы ↔ S ---- */
  const viewOf = (): PshootView => ({
    cat: S.cat,
    set: h.setK(),
    own: (S.own[S.cat] ?? []).slice(),
    ref: h.ref.active(),
    n: S.n,
    plat: S.plat,
    fmt: S.fmt,
    pick: h.pick(),
    wishes: S.text,
  });
  /** Тот же ли вид (входы → S не нужны): набор, свой набор, свои фото сцен, число, площадка, формат, модель, пожелания */
  const sameView = (a: PshootView, b: PshootView) =>
    JSON.stringify([a.cat, a.ref ? 'ref' : a.set, a.set === 'own' && !a.ref ? a.own : [], a.ref ? 0 : a.n, a.plat, a.fmt, a.pick.who, a.pick.id ?? null, a.wishes]) ===
    JSON.stringify([b.cat, b.ref ? 'ref' : b.set, b.set === 'own' && !b.ref ? b.own : [], b.ref ? 0 : b.n, b.plat, b.fmt, b.pick.who, b.pick.id ?? null, b.wishes]);
  /** Выбор модели из входов → ряд «Кто в кадре» (BotPeople в режиме «модель»: близкий и персонаж — S.people, «Модель ИИ» — S.mc, «Вы» — в кадре) */
  function applyPick(p: SnapPick) {
    /* новый в ряду — без действия (act null, как у BotPeople) */
    const one = (id: number | string) => [{ id, act: null, own: '' } as unknown as SelItem];
    S.people = p.who === 'person' && p.id !== null ? one(p.id) : p.who === 'char' ? one(`c:${p.id}`) : [];
    S.extra = [];
    S.mc = p.who === 'tile' ? 'gen' : null;
    h.people.setIn(p.who === 'me');
  }
  /** Вид входов → S (вход экрана, повтор, «Изменить», заявка родной формы) */
  function apply(f: Frame, v: PshootView, inputs: Record<string, string>) {
    S.cat = v.cat;
    S.setK = { ...S.setK, [v.cat]: v.set };
    if (v.set === 'own') S.own = { ...S.own, [v.cat]: v.own.slice() };
    S.n = v.n;
    S.plat = v.plat;
    S.platVia = false;
    S.fmt = v.fmt;
    S.text = v.wishes;
    S.focus = null;
    applyPick(v.pick);
    /* свои фото сцен: превью загрузок из входов (решение Q7: не открылось — загрузить заново); без них — сняты */
    const ids = f.ref.ids(inputs);
    const have = h.ref.order().map(item => item.upload_id).filter(Boolean);
    if (v.ref && ids.length) {
      if (!h.ref.active() || have.join() !== ids.join()) restoreRef(f, inputs);
    } else if (h.ref.files().length) h.ref.restore(null);
    /* «Съёмка» из входов — когда словари сервера уже есть (иначе — по их приходу) */
    if (f.cam.has()) f.cam.restore(inputs.shoot);
  }
  function restoreRef(f: Frame, inputs: Record<string, string>) {
    f.refRestoring = true;
    void f.ref.restore(inputs).then(res => {
      if (!res) return;
      f.refRestoring = false;
      if (cur() !== f) return;
      if (res.files.length) h.ref.restore({ files: res.files });
      else h.ref.restore(null);
      if (res.text) h.ref.say(res.text);
      if (h.mounted()) changed(f);
    });
  }
  /** «Съёмка» во входы: блок только при совпадении словарей (null — ключ снять); словари ещё не пришли — ключ не трогать */
  function shootValue(f: Frame): string | null | undefined {
    const form = f.svc.get().form;
    if (!pshootKeys(form).shoot) return undefined;
    if (!f.camDone) return undefined;
    return f.cam.order() ?? null;
  }
  /** S → входы: одна точка записи (syncFoot шаблона); сервис заперт — запись не идёт (зеркало) */
  function commit(f: Frame) {
    const form = f.svc.get().form;
    const L = f.lists;
    if (!form || !L || !f.entered || f.form !== form) return;
    const model = modelOf(h.pick(), safePeople());
    const shoot = shootValue(f);
    const setKey = pshootKeys(form).set;
    f.mirror.commit(prev => {
      const view = viewOf();
      let refs = h.nNow();
      /* превью своих фото сцен из входов ещё читается: их ключи, set_id 'ref' и число фото остаются */
      if (f.refRestoring) {
        view.ref = !!setKey && valueOf(form, prev, setKey) === 'ref';
        refs = f.ref.ids(prev).length;
      }
      const next = writePshoot(form, L, view, prev, { model, refs, shoot });
      return f.refRestoring ? next : f.ref.inputsFor(next, h.ref);
    });
  }

  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  function layoutOf(f: Frame) {
    const L = f.lists;
    if (!L) return '';
    return JSON.stringify([
      L.cats.map(item => item[0]),
      L.mps.map(item => item[0]),
      L.fmts.map(item => item[0]),
      L.counts,
      Object.entries(L.sets).map(([cat, list]) => [cat, list.map(item => item.id)]),
      (['format', 'count', 'platform', 'shoot', 'wishes', 'model', 'gen', 'char', 'scenes'] as const).map(k => server.has(k)),
      server.refOn(),
      server.unit(),
      server.improveKind(),
      !!L.example,
    ]);
  }
  /** Форма, документ и входы → списки шаблона и S (без отрисовки); true — форму надо нарисовать заново */
  function syncState(f: Frame): boolean {
    const { s } = live(f);
    const form = s.form;
    const data = f.doc.data();
    if (!form || !data) return false;
    let repaint = false;
    /* форма перечитана («Обновить форму», «К настройкам» после смены цены или версии): сервис сбросил входы к началу кадра, а выбор
       человека остаётся в S и уходит в новые входы следующей записью (шаблон: назад к форме с теми же настройками) */
    const reread = f.entered && !!f.form && f.form !== form;
    const external = f.entered && !reread && f.mirror.external(s.inputs);
    if (f.form !== form || f.data !== data || filled !== f || external) {
      if (f.form !== form) {
        const keys = photoSlots(form)
          .slice(0, 5)
          .map(field => field.key);
        f.tray = keys.length ? f.media.slots(keys) : null;
      }
      f.form = form;
      f.data = data;
      f.lists = pshootLists(data, form);
      fillLists(f);
    }
    const L = f.lists!;
    if (!f.entered) {
      apply(f, readPshoot(form, L, s.inputs), s.inputs);
      f.entered = true;
      f.mirror.seen(s.inputs);
      repaint = true;
    } else if (reread) {
      /* выбор человека — в S; то, чего в новой форме нет, — к значениям по умолчанию */
      if (!L.cats.some(item => item[0] === S.cat)) S.cat = L.defaults.cat;
      if (!L.counts.includes(S.n)) S.n = L.defaults.n;
      if (!L.mps.some(item => item[0] === S.plat)) S.plat = L.defaults.plat;
      if (!L.fmts.some(item => item[0] === S.fmt)) S.fmt = L.defaults.fmt;
      f.mirror.seen(s.inputs);
      repaint = true;
    } else if (external) {
      /* входы изменили не зеркалом: мосты загрузок (слоты фото), повтор, «Изменить», заявка родной формы — в S только другой вид */
      const v = readPshoot(form, L, s.inputs);
      const ids = f.ref.ids(s.inputs).join();
      const have = h.ref.order().map(item => item.upload_id).filter(Boolean).join();
      if (!sameView(v, viewOf()) || (v.ref && ids !== have && !f.refRestoring)) {
        apply(f, v, s.inputs);
        repaint = true;
      }
      f.mirror.seen(s.inputs);
    }
    const layout = layoutOf(f);
    if (layout !== f.layout) {
      f.layout = layout;
      repaint = true;
    }
    return repaint;
  }
  function formSync(f: Frame) {
    const repaint = syncState(f);
    if (!onForm()) return;
    if (repaint) h.paintPage();
    if (f.svc.get().form) photosSync(f);
    /* ошибка формы (файлы повтора недоступны, форма не загрузилась) — один раз в рамке */
    const now = f.svc.get();
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      /* отказ проверки файла рамка или карточка референса уже назвала у самого файла (media-bridge: say по ключу загрузки): отказ своего фото
         сцены не повторяется под лотком товара */
      if (error && error !== TRANSPORT && error !== REJECTED) h.up.say(uiText(error));
    }
    h.syncFoot();
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    filesSync(f);
    formSync(f);
    f.media.sync();
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту) */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.syncFoot();
      });
  }
  /** Что починить вместо создания: форма или сцены не загрузились, форма устарела — перечитать; загрузка не подтверждена — продолжить тем же
      id; фото повтора без подтверждённых прав — подтвердить */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (s.form && f.doc.error()) return () => void f.doc.reload();
    if (f.media.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.media.resume());
    if (s.form && rightsNeeded(f)) return () => confirmRights(f);
    return null;
  }
  /** Словари «Съёмки» (один раз на кадр): пришли — блок показывается, если совпадают; выбор из входов */
  function camLoad(f: Frame) {
    if (f.camAsked) return;
    f.camAsked = true;
    void f.cam.load().then(() => {
      f.camDone = true;
      if (cur() !== f) return;
      if (f.cam.has() && f.entered) f.cam.restore(f.svc.get().inputs.shoot);
      if (h.mounted()) changed(f);
    });
  }
  /** Скидка «дешевле» формы — только для той цены, для которой она посчитана (цена формы, D11) */
  function cheapOffer(f: Frame | null) {
    const form = f?.svc.get().form;
    const offer = studioCheapOffer(form ?? null);
    return offer && form && server.price(false) === form.expected_price ? offer : null;
  }

  /* ---- вход заказа: тело заявки (или подсмотренной), иначе форма ---- */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  const bodyInputs = (it: Intent) =>
    Object.fromEntries(Object.entries(it.body.inputs as Record<string, unknown>).map(([key, value]) => [key, typeof value === 'string' ? value : JSON.stringify(value)]));
  /** Площадка или свой формат строкой PLAT */
  const platRow = (L: PshootLists | null | undefined, plat: string, fmt: string): PlatRow => {
    const rows = h.PLAT.length ? h.PLAT : (L?.plat ?? []);
    return rows.find(row => row[0] === (fmt && fmt !== 'mp' ? fmt : plat)) ?? rows[0] ?? [plat, plat, '3:4', 1200, 1600];
  };

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const paint = typeof opts.paint === 'function' ? (opts.paint as (r: BotRunRun) => void) : null;
    const more = typeof opts.more === 'function' ? (opts.more as (r: BotRunRun) => string) : null;
    inst = create({
      ...opts,
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      /* подписи и тексты страницы заказа — на языке профиля в момент показа (BotRun берёт их через val: функции) */
      foot: chatFoot(serverFoot({ ...((opts.foot as Record<string, unknown>) ?? {}), mod: ['data-ps-again', 'edit', () => uiText('Изменить пожелания')] }, 'ps'), 'ps'),
      T: {
        ...((opts.T as Record<string, unknown>) ?? {}),
        runH: () => uiText('Снимаем товар'),
        runP: () => uiText('Можно уйти с экрана: бот пришлёт фото в чат, как только набор будет готов.'),
        doneH: () => uiText('Фото товара готовы'),
        doneP: (r: BotRunRun) => server.doneText(r),
        modH: () => uiText(FAIL.modH),
        techH: () => uiText(FAIL.techH),
        notMade: () => uiText(FAIL.notMade),
        modStep: () => uiText('Не прошло проверку'),
        sent: () => uiText('фото'),
      },
      head: { ...((opts.head as Record<string, unknown>) ?? {}), title: () => uiText('Фотосессия товара') },
      /* смена страницы start прежнего хвоста (SteosUI.swap, 170 мс) может прийти после смены кадра: его RUN уже не хвост экрана — без тела */
      ...(more ? { more: (r: BotRunRun) => (inst && inst.run !== r ? '' : more(r)) } : {}),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      /* ход кадров — подшаг сервера (progress.done, run-driver k); без него счётчика и полосы нет (решение Q-S3):
         пустая полоса на нуле выглядела лишней серой линией под текстом */
      paint: (r: BotRunRun) => {
        const run = r as BotRunRun & { done?: number };
        const k = typeof r.k === 'number' ? r.k : null;
        run.done = k === null ? 0 : Math.max(0, Math.min(k, Number(r.snap.n) || 0));
        paint?.(r);
        const n = h.root()?.querySelector<HTMLElement>('#psPrgN');
        if (k === null && n) n.textContent = '';
        const bar = n?.parentElement;
        if (bar && k === null) bar.style.display = 'none';
        else if (bar?.style.display) {
          bar.style.removeProperty('display');
          if (!bar.getAttribute('style')) bar.removeAttribute('style');
        }
      },
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        ((inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key])(...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });
  /* start и retry меняют страницу через SteosUI.swap (170 мс), а его отложенная отрисовка читает RUN BotRun: уход с экрана в эти мс
     (close шаблона — TAIL.drop) снимал бы RUN раньше неё. Снятие ждёт смену страницы и не идёт, если экран за это время открыли снова */
  let swapUntil = 0;
  const call = (key: 'start' | 'retry' | 'drop' | 'sync') => (inst as unknown as Record<string, () => void>)[key]();
  facade.start = () => {
    swapUntil = Date.now() + 200;
    call('start');
  };
  facade.retry = () => {
    swapUntil = Date.now() + 200;
    call('retry');
  };
  facade.drop = () => {
    const left = swapUntil - Date.now();
    const r = inst?.run;
    if (!inst || !r || left <= 0) {
      if (inst) call('drop');
      return;
    }
    const mine = inst;
    window.setTimeout(() => {
      if (inst !== mine || mine.run !== r || h.mounted()) return;
      call('drop');
      call('sync');
    }, left);
  };

  /* ---- итог: кадры по секциям ---- */
  /** Раскладка итога: секции ответа (сцены) с номерами файлов-картинок; без секций — по сценам заказа, если число кадров сходится, иначе одной
      группой */
  function relayout(r: BotRunRun, f: Frame): boolean {
    const order = f.svc.get().order;
    const snap = r.snap as PshootSnap;
    const images = (order?.result?.assets ?? [])
      .filter(asset => RESULT_LIMITS.mimes!.test(asset.mime))
      .map(asset => asset.index)
      .sort((a, b) => a - b);
    if (!images.length) return false;
    const known = new Set(images);
    const secs = sections(order)
      .map(section => ({ n: section.title, idx: section.indices.filter(i => known.has(i)) }))
      .filter(section => section.idx.length);
    const groups0 = Array.isArray(snap.sc) ? snap.sc : [];
    let groups: Array<{ n: string; idx: number[] }>;
    if (secs.length) groups = secs.map((section, i) => ({ n: section.n || groups0[i]?.n || uiText('Фото товара'), idx: section.idx }));
    else if (groups0.reduce((sum, g) => sum + g.k, 0) === images.length) {
      let at = 0;
      groups = groups0.map(g => ({ n: g.n, idx: images.slice(at, (at += g.k)) }));
    } else groups = [{ n: groups0.length === 1 ? groups0[0].n : uiText('Фото товара'), idx: images }];
    const next = groups.map((g, i) => ({ id: `r${i}`, n: g.n, k: g.idx.length }));
    const idx = groups.flatMap(g => g.idx);
    const sig = (x: { sc: unknown; idx: unknown }) => JSON.stringify([x.sc, x.idx]);
    if (sig({ sc: next, idx }) === sig({ sc: snap.sc, idx: snap.idx })) return false;
    Object.assign(snap, { sc: next, idx, n: idx.length });
    return true;
  }
  /** Кадры, которые пришли, — в кнопки итога на месте (якоря не пересоздаются) */
  function paintResults() {
    h.root()
      ?.querySelectorAll<HTMLElement>('#psRunB [data-ps-res]')
      .forEach(btn => {
        if (btn.querySelector('img')) return;
        const pic = server.resPic(Number(btn.dataset.psRes));
        if (pic) btn.insertAdjacentHTML('afterbegin', pic);
      });
  }
  /** Заказ готов (каждая сверка): раскладка по секциям и кадры — один раз на заказ и ревизию */
  function onDone(f: Frame) {
    const order = f.svc.get().order;
    const r = h.tail.run;
    if (!order || order.status !== 'done' || !r || r.st !== 'done') return;
    const key = galleryKey(order);
    if (f.doneKey === key && f.doneRun === r) return;
    f.doneKey = key;
    f.doneRun = r;
    if (relayout(r, f)) h.tail.refresh();
    void f.gallery.load(order).then(items => {
      if (!items || h.tail.run !== r || cur() !== f) return;
      paintResults();
    });
  }
  /** Файл итога кадра k (null — ещё не пришёл) */
  function resultItem(f: Frame | null, k: number) {
    const order = f?.svc.get().order;
    const index = (h.tail.run?.snap as PshootSnap | undefined)?.idx?.[k];
    if (!f || !order || index === undefined) return null;
    return f.gallery.items(order).find(item => item.index === index) ?? null;
  }
  /** «Слайды с преимуществами», «Видео товара, 5 сек»: экран с возвратом после сверки владельца (родной хвост: openStudioTarget; файлы — Q-S6) */
  function handoff(f: Frame, screen: 'infog' | 'video', el: HTMLElement | null) {
    const s = f.svc.get();
    const r = h.tail.run;
    if (s.order?.status !== 'done' || r?.st !== 'done' || f.moving) return;
    f.moving = true;
    const target = screen === 'infog' ? { screen: 'infog', ret: true } : { screen: 'video', options: { mode: 'product' }, ret: true };
    void busyButton(el, () => handoffTarget({ ownerId: s.identity?.owner, target }))
      .catch(() => {
        if (!el || el.isConnected) tip(el, 'Не удалось открыть экран. Попробуйте ещё раз.');
      })
      .finally(() => {
        f.moving = false;
      });
  }
  /** «Назад» внутри экрана (Telegram, «назад» раздела): сначала верхний слой — лайтбокс итога, «Улучшить», поле во весь экран, лист экрана,
      меню категории, лист людей или ядра */
  function back(f: Frame): boolean {
    if (cur() !== f || !h.mounted()) return false;
    const bc = core();
    if (bc.rv?.lightbox?.isOpen) {
      bc.rv.lightbox.close();
      return true;
    }
    if (bc.improve?.isOpen) {
      bc.improve.close();
      return true;
    }
    if (bc.editor?.isOpen) {
      bc.editor.close();
      return true;
    }
    if (S.sh) {
      h.sheetClose();
      return true;
    }
    if (S.menu) {
      h.menuClose();
      return true;
    }
    if (h.people.isOpen) {
      h.people.close();
      return true;
    }
    const sheets = [...document.querySelectorAll<HTMLElement>('.bsheet.open[aria-hidden="false"]')];
    const top = sheets.at(-1);
    const x = top?.querySelector<HTMLElement>('[data-bsheet-x],[data-ps-shx],.xbtn');
    if (!x) return false;
    x.click();
    return true;
  }

  /* ---- привязка кадра ---- */
  let watching = false;
  /** «Вы» и близкие сменились (server-state, листы людей, другой экран): ряд лиц на месте, лента наборов, кнопка и входы — заново */
  function watch() {
    if (watching) return;
    watching = true;
    watchPeople({
      paint: () => h.people.paintForm(),
      mounted: () => !!cur() && onForm(),
      changed: () => {
        h.railSync();
        h.syncFoot();
      },
      m: safePeople,
    });
  }
  function dropFrame(f: Frame) {
    f.consent?.close();
    f.consent = null;
    f.media.cancel();
    f.ref.cancel();
    f.gallery.release();
    f.doc.abort();
  }
  function bind(b: EngineBinding): EngineBound {
    watch();
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) dropFrame(frame);
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      back: () => back(mine),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      tray: null,
      consent: null,
      form: null,
      data: undefined,
      lists: null,
      entered: false,
      layout: '',
      restored: '',
      refRestoring: false,
      camAsked: false,
      camDone: false,
      said: '',
      peek: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      doneKey: '',
      doneRun: null,
      moving: false,
    });
    f.doc = screenDoc<Record<string, unknown>>('pshoot', { remember: true, onChange: guard(() => changed(f)) });
    f.media = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string, key: string) => (f.ref.keys().includes(key) ? h.ref.say(text) : h.up.say(text))),
      put: guard(() => trayChanged(f)),
      drop: guard(() => trayChanged(f)),
      update: guard(() => {
        trayChanged(f);
        if (cur() === f && onForm()) h.syncFoot();
      }),
      consentWith: () => ({ texts: CONSENT }),
    });
    f.ref = createRefBridge({ media: f.media, svc: b.svc, owner: () => f.svc.get().identity?.owner });
    f.cam = createCamBridge({ block: () => h.cam, form: () => f.svc.get().form, inputs: () => f.svc.get().inputs });
    f.gallery = createResultGallery(RESULT_LIMITS);
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => h.paintPage(),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
      stepAlias: STEP_ALIAS,
      progress: order => tailProgress(order)?.done,
    });
    return f;
  }

  const refOpts: RefOpts = {
    upload: (file, e) => {
      const f = cur();
      return f ? f.ref.opts().upload(file, e) : Promise.resolve({});
    },
    remove: e => frame?.ref.opts().remove(e),
  };

  const server: PshootServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    /* вход экрана — входы сервиса (повтор, «Изменить»): в setup шаблона ничего не идёт, S ставит сверка формы */
    entry: () => ({}),
    open() {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: фото, наборы, модель, свои фото сцен, «Съёмка», скидка и хвост прежнего сюда не
           переходят */
        h.sheetClose();
        h.menuClose();
        h.camStop();
        h.people.close();
        h.cam.reset();
        h.ref.restore(null);
        Object.assign(S, {
          cat: '',
          setK: {},
          own: {},
          focus: null,
          platVia: false,
          n: 4,
          people: [],
          extra: [],
          mc: null,
          files: [],
          text: '',
          cheap: false,
          menu: null,
          sh: null,
          ret: false,
        });
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
        f.data = undefined;
        f.entered = false;
      }
      f.formShown = false;
      S.files = filesOf(f);
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      void f.doc.load();
      camLoad(f);
      h.people.prune();
      /* входы → S до первой отрисовки: подвал формы пишет S обратно во входы */
      syncState(f);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      /* загрузки, превью и согласие загрузок — у мостов кадра: уход их не трогает (заказ и загрузки ведёт сервис); свой лист прав — закрыть */
      const f = frame;
      f?.consent?.close();
      if (f) f.consent = null;
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { go?: unknown; where?: unknown };
      /* скидку «Сделать дешевле» публикует форма, и она посчитана для цены формы (D11): без неё у подвала нет ни цены, ни действия скидки */
      return {
        get where() {
          return typeof cheap.where === 'string' ? uiText(cheap.where) : cheap.where;
        },
        get price() {
          return cheapOffer(cur()) ? () => server.price(true) : undefined;
        },
        get go() {
          return cheapOffer(cur()) ? cheap.go : undefined;
        },
        get percent() {
          return cheapOffer(cur())?.percent;
        },
      } as unknown as T;
    },
    has(k) {
      const f = cur() ?? frame;
      const form = f?.svc.get().form ?? null;
      const L = f?.lists;
      if (!f || !form || !L) return false;
      const keys = pshootKeys(form);
      const modelField = keys.model ? fieldOf(form, keys.model) : undefined;
      switch (k) {
        case 'format':
          return !!keys.format && L.fmts.length > 0;
        case 'count':
          return !!keys.count;
        case 'platform':
          return !!keys.platform && L.mps.length > 0;
        case 'shoot':
          return !!keys.shoot && f.cam.has();
        case 'wishes':
          return !!keys.wishes;
        case 'model':
          return !!keys.model;
        case 'gen':
          return allowed(modelField, 'gen');
        case 'char':
          return !!keys.avatarId && allowed(modelField, 'char');
        default:
          return !!keys.set && !!keys.scenes;
      }
    },
    refOn() {
      const f = cur() ?? frame;
      return !!f?.lists && f.ref.has();
    },
    refOpts: () => refOpts,
    defSet(cat) {
      const L = (cur() ?? frame)?.lists;
      return L ? defaultSet(L, cat) : '';
    },
    gen: () => ({ img: SILHOUETTE, y: 30 }),
    shot(o) {
      const sc = o.sc;
      const cls = o.cls ? ` ${h.esc(o.cls)}` : '';
      const ex = sc?.ex ?? [];
      const src = sc?.bg || (ex.length ? ex[(o.v ?? 0) % ex.length] : '') || sc?.url || '';
      return src
        ? `<span class="pd-ph${cls}"><img class="pd-bg" src="${h.esc(src)}" alt="" draggable="false"></span>`
        : `<span class="pd-ph${cls}"></span>`;
    },
    exN: sc => Math.max(1, sc?.ex?.length ?? 0),
    example() {
      const L = (cur() ?? frame)?.lists;
      const x = L?.example;
      if (!x) return null;
      /* у шаблона пример рамки — товар выбранной категории (exOf: exURL(S.cat)) */
      const own = L.examples?.[S.cat];
      return { before: own?.before ?? x.before, after: own?.after ?? x.after, labels: x.labels.map(label => uiText(label)), title: x.title || uiText('Загрузите фото товара'), text: x.text };
    },
    upOpts() {
      const f = cur() ?? frame;
      const key = f?.tray?.keys()[0];
      const policy = f && key ? mediaPolicy(f.svc.get().form, key) : null;
      return {
        ...(policy ? { accept: policy.accept.join(','), mb: Math.round(policy.maxBytes / MB) } : {}),
        /* свои строки рамки BotCore.upload («Загрузить», «Снять», «Можно до N фото», «N из M») — словарём профиля */
        tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      };
    },
    setImg() {
      const first = h.sel()[0];
      const scene = h.SCENES.find(item => item.id === first);
      return scene?.url || scene?.ex[0] || '';
    },
    camBase: () => ({ img: SILHOUETTE, prod: S.files[0]?.st === 'ok' ? S.files[0].url : '' }),
    unit() {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      const L = f?.lists;
      if (!form || !L || L.unit === null || form.chargeable === false) return '';
      return core().html.unit(L.unit, uiText(L.unitWord));
    },
    nUnit(n) {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      if (!f || !form || form.chargeable === false) return '';
      const keys = pshootKeys(form);
      const listed = record(rawField(form, keys.count).prices)[String(n)];
      const value = typeof listed === 'number' && Number.isSafeInteger(listed) && listed >= 0 ? listed : f.lists?.unit != null ? f.lists.unit * n : null;
      return value === null ? '' : core().html.unit(value);
    },
    improveKind() {
      const f = cur() ?? frame;
      const form = f?.svc.get().form;
      if (!form || !pshootKeys(form).wishes) return '';
      const kind = record(record(form.constraints).pshoot).improvement_kind;
      return isImproveKind(kind) ? kind : '';
    },
    price(cheap) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const form = s?.form;
      if (!f || !s || !form) return toolPrice(f?.app.price).amount ?? 0;
      if (cheap) {
        const offer = cheapOffer(f);
        if (offer) return offer.price;
      }
      /* расчёт сервера под нынешний состав — цена кнопки; до него — оценка по цене кадра прайса */
      const d = f.svc.derive();
      const unit = f.lists?.unit;
      if (form.chargeable === false || d.quoted || unit === null || unit === undefined) return d.price;
      return unit * h.nNow();
    },
    missing() {
      const f = cur();
      if (!f) return 'load';
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return '';
      if (!s.identity && !s.integrityError) return 'load';
      if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
      /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
      if (!s.form) return s.error ? '' : 'load';
      if (s.versionStale) return '';
      if (!pshootSupported(s.form)) return 'off';
      if (f.doc.error()) return '';
      if (!f.lists || !f.entered) return 'docs';
      if (!s.form.available) return 'unavailable';
      /* чинить, а не создавать: «Продолжить загрузку», «Подтвердите права на фото» — кнопка активна */
      if (recovery(f)) return '';
      return null;
    },
    ready() {
      const f = cur();
      if (!f) return uiText('Проверяем доступность');
      const { s, d } = live(f);
      const form = s.form;
      if (!form || !f.lists) return uiText('Проверяем доступность');
      if (f.media.pending()) return uiText('Загружаем фото');
      if (f.refRestoring) return uiText('Проверяем фото');
      const p = h.pick();
      if (p.who === 'person') {
        const ref = personRef(p.id, safePeople());
        if (!ref.ok) return ref.text;
      }
      if (S.extra.length) return uiText('Этот человек не участвует');
      const keys = pshootKeys(form);
      /* модель, которую нельзя отправить (персонаж без id, выбор, которого форма не публикует), — не молча «Без модели» */
      const choice = modelOf(p, safePeople()).model;
      if (keys.model && ((p.who !== 'none' && choice === 'none') || !allowed(fieldOf(form, keys.model), choice))) return uiText('Этот человек не участвует');
      const handled = [...Object.values(keys).filter((key): key is string => !!key), ...photoSlots(form).map(field => field.key), ...f.ref.keys()];
      const gap = requiredGap(form, s.inputs, handled);
      if (gap) return uiText(gap);
      if (form.chargeable !== false && server.price(false) !== d.price) return uiText('Цена этого выбора пока не подтверждена');
      return '';
    },
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText(s?.error || 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'docs':
          return uiText('Загружаем сцены…');
        case 'cut':
          return uiText('Загружаем фото');
        default:
          return '';
      }
    },
    label() {
      const f = cur();
      if (!f) return null;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale || (s.form && f.doc.error())) return uiText('Обновить форму');
      if (f.media.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (s.form && rightsNeeded(f)) return uiText('Подтвердите права на фото');
      if (s.form?.chargeable === false) return uiFormat('Создать {0}', photosW(h.nNow()));
      return null;
    },
    write() {
      const f = cur();
      if (!f || !onForm()) return;
      commit(f);
    },
    pick(files) {
      const f = cur();
      const list = [...(files ?? [])];
      if (!f || !list.length) return;
      if (!f.tray) {
        h.up.say(uiText('Проверяем доступность'));
        return;
      }
      void f.tray.pickSlots(list);
    },
    remove(i) {
      const f = cur();
      const id = S.files[i]?.id;
      if (!f?.tray || !id) return;
      f.tray.remove(id);
      trayChanged(f);
    },
    main(i) {
      const f = cur();
      const id = S.files[i]?.id;
      if (!f?.tray || !id || i === 0) return;
      const ids = f.tray.list().map(item => item.id);
      f.tray.order([id, ...ids.filter(other => other !== id)]);
      trayChanged(f);
    },
    replace(i, file) {
      const f = cur();
      const id = S.files[i]?.id;
      if (!f?.tray || !id) return;
      const at = f.tray.list().findIndex(item => item.id === id);
      if (at >= 0) void f.tray.replaceAt(at, file);
    },
    camText: () => uiText('Камера недоступна. Загрузите файл.'),
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    steps() {
      const row = h.plat();
      const pack = row?.[5] ? uiFormat('Готовим файлы в формате «{0}»', row[1]) : uiFormat('Готовим файлы под {0}', row?.[1] ?? '');
      return [
        ['check', 'image', uiText('Проверяем фото'), uiText('Фото проверены')],
        ['cut', 'scissors', uiText('Убираем фон'), uiText('Фон убран')],
        ['shoot', 'sparkle', uiText('Снимаем сцены'), uiText('Сцены сняты')],
        ['pack', 'download', pack, uiText('Файлы готовы')],
      ];
    },
    snap() {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const form = s?.form ?? null;
      const L = f?.lists ?? null;
      const it = intentOf(f);
      const inputs = it ? bodyInputs(it) : null;
      const view = inputs && L ? readPshoot(form, L, inputs) : viewOf();
      const keys = pshootKeys(form);
      const sent = inputs && keys.count ? Number(valueOf(form, inputs, keys.count)) : NaN;
      const n = view.ref ? (Number.isSafeInteger(sent) && sent > 0 ? sent : h.nNow()) : view.n;
      const name = (id: string) => h.SCENES.find(item => item.id === id)?.n ?? id;
      const sc: PshootGroup[] = view.ref
        ? [{ id: 'ref', n: uiText('По вашим фото'), k: n }]
        : L
          ? splitScenes(viewScenes(L, view), n).map(item => ({ id: item.scene_id, n: name(item.scene_id), k: item.count }))
          : [];
      const cat = (L?.cats ?? h.CATS).find(item => item[0] === view.cat);
      const row = platRow(L, view.plat, view.fmt);
      const choice = modelOf(view.pick, safePeople());
      return {
        cat: view.cat,
        item: cat?.[2] ?? cat?.[1] ?? '',
        plat: view.plat,
        fmt: view.fmt,
        pn: row[1],
        ar: row[2],
        px: [row[3], row[4]],
        n,
        set: view.ref ? 'ref' : view.set,
        sc,
        files: [],
        model: choice.model === 'person' ? `person:${choice.person_id}` : choice.model === 'char' ? `char:${choice.avatar_id}` : choice.model,
        m: null,
        mn: view.pick.who === 'none' ? '' : h.pickName(view.pick).replace(/^Вы$/, uiText('вы')),
        text: view.wishes.trim(),
        cheap: !!(it ? it.body.cheap : S.cheap),
        idx: [],
      };
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      /* у шаблона сбой без «на шаге …» (T.techStep:false хвоста экрана): этап сбоя не называется */
      return failText(r, { order, error: s ? s.integrityError || s.error : '', texts: FAIL });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'на фото' });
    },
    doneText(r) {
      const f = cur() ?? frame;
      const order = f?.svc.get().order ?? null;
      const snap = r.snap as PshootSnap;
      const n = Number(snap.n) || 0;
      const groups = Array.isArray(snap.sc) ? snap.sc.length : 0;
      /* doneP шаблона: «4 фото в 2 сценах, 1200 × 1600 под Wildberries» (свои фото сцен — «по вашим сценам») */
      const where =
        snap.set === 'ref'
          ? uiFormat('{0} по вашим сценам', photosW(n))
          : uiFormat('{0} в {1}', photosW(n), plural(groups, uiText('сцене'), uiText('сценах'), uiText('сценах')));
      const px = Array.isArray(snap.px) ? `<span class="mono">${snap.px[0]} × ${snap.px[1]}</span>` : '';
      /* родной хвост: бот уже прислал (доставка sent, delivered) или пришлёт; не присылает — текст доставки (tail-done) */
      const sent = ['sent', 'delivered'].includes(String(record(record(order).delivery).state));
      const what = `${esc(where)}, ${px} ${esc(uiFormat('под {0}', String(snap.pn ?? '')))}`;
      const base = uiFormat(sent ? '{0}. Бот уже прислал их в чат, в отдельную тему.' : '{0}. Бот пришлёт их в чат, в отдельную тему.', what);
      return doneP(order, base, 'ps');
    },
    resPic(k) {
      const item = resultItem(cur(), k);
      return item ? `<img src="${esc(item.url)}" alt="" draggable="false">` : '';
    },
    lbText() {
      const LB_TEXT: Record<string, string> = { save: 'Скачать', x: 'Закрыть', prev: 'Предыдущий', next: 'Следующий' };
      document.querySelectorAll<HTMLElement>('.rv-lb [data-rvlb]').forEach(btn => {
        const text = LB_TEXT[btn.dataset.rvlb ?? ''];
        if (text) btn.setAttribute('aria-label', uiText(text));
      });
    },
    async save(k) {
      const item = resultItem(cur(), k);
      if (!item) throw new Error(uiText('Кадры ещё загружаются'));
      return { data: item.blob, name: `steos-product-${pad(k + 1)}${extOf(item.mime)}` };
    },
    saveAll(btn) {
      const f = cur();
      const order = f?.svc.get().order;
      if (!f || order?.status !== 'done') return;
      const items = f.gallery.items(order);
      const bc = core();
      if (!items.length || !bc.saveBtn) {
        tip(btn, 'Кадры ещё загружаются');
        return;
      }
      const ui = h.ui();
      /* архив итога (owner-protected путь заказа), иначе один кадр — сам файл, несколько — zip кадров (главное — первым) */
      bc.saveBtn(
        btn,
        async () => {
          if (archiveUrl(order)) return { data: await archive(order), name: 'steos-product-photos.zip' };
          if (items.length === 1) return { data: items[0].blob, name: `steos-product-photo${extOf(items[0].mime)}` };
          if (!bc.zip) throw new Error('zip_unavailable');
          return { data: await bc.zip(items.map((item, k) => ({ name: `photo-${pad(k + 1)}${extOf(item.mime)}`, data: item.blob }))), name: 'steos-product-photos.zip' };
        },
        { buzz: () => ui.buzz(), tip: (el: Element, text: string) => ui.tip(el, text) },
      );
    },
    next(screen) {
      const f = cur();
      if (!f) return;
      const el = h.root()?.querySelector<HTMLElement>(screen === 'infog' ? '[data-ps-infog]' : '[data-ps-vcover]') ?? null;
      handoff(f, screen, el);
    },
    leave(go) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(go, false, '');
    },
    click(d, el) {
      const f = cur();
      const s = f?.svc.get();
      const r = h.tail.run;
      if (d.psChat !== undefined) {
        if (r?.st === 'done') chatClick({ order: s?.order, el, text: 'Тут откроется тема чата, куда бот прислал фото', tip: (at, text) => h.ui().tip(at, text) });
        return true;
      }
      if (d.psSettings !== undefined) {
        openSettings();
        return true;
      }
      if (d.psRetry === undefined && d.psRchk === undefined && d.psFix === undefined) return false;
      if (!f || f.driver.busy()) return true;
      if (d.psRetry !== undefined) h.tail.retry();
      else if (d.psRchk !== undefined) void f.driver.check(el);
      else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, '');
      return true;
    },
  };

  /* ---- смена языка профиля (i18n: событие steos:locale) ---- */
  /** Подписи этапов идущего заказа — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  /** Открытый экран рисуется заново на новом языке: списки шаблона (форматы), хвост — TAIL.show, форма — paintPage */
  function relocale() {
    const r = h.tail.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (f.lists) fillLists(f);
    if (r) h.tail.show(root);
    else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  /* новый снимок Me уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.syncFoot();
  });

  instance = { bind };
  return server;
}

/** ENGINES.pshoot.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindPshootEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «pshoot» реестра движков студии: движок шаблона window.BotPShoot, атрибуты data-ps-*, правая панель (каталог персонажей для модели),
    знак заказа «pshoot» (Q-S8) */
export const PSHOOT_ENGINE: EngineScreen = { global: 'BotPShoot', prefix: 'ps', side: true, kind: 'pshoot', bind: bindPshootEngine };

export function registerPshootEngine() {
  ENGINES.pshoot = PSHOOT_ENGINE;
}
